import { useRef, useState } from "react";
import { ChevronsUpDown, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

function formatCategoryName(name: string): string {
  if (!name) return name;
  return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

interface CategoryPickerProps {
  value: string;
  onChange: (name: string) => void;
  categories: { id: string; name: string }[];
  onCreateCategory?: (name: string) => void;
  placeholder?: string;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
}

export function CategoryPicker({
  value,
  onChange,
  categories,
  onCreateCategory,
  placeholder = "Select category",
  ariaLabel,
  ariaLabelledBy,
  className,
}: CategoryPickerProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [addMode, setAddMode] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const pickerCategories =
    !value || categories.some((c) => c.name === value)
      ? categories
      : [...categories, { id: `current-${value}`, name: value }];

  const filtered = pickerCategories.filter(
    (c) =>
      !search ||
      formatCategoryName(c.name)
        .toLowerCase()
        .includes(search.toLowerCase()),
  );

  const showCreate =
    !!onCreateCategory &&
    search.trim() !== "" &&
    !pickerCategories.some(
      (c) =>
        formatCategoryName(c.name).toLowerCase() ===
        search.trim().toLowerCase(),
    );

  const commit = (name: string, created: boolean) => {
    onChange(name);
    if (created) onCreateCategory?.(name);
    setOpen(false);
    setSearch("");
    setAddMode(false);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setSearch("");
          setAddMode(false);
        }
      }}
    >
      <PopoverTrigger
        role="combobox"
        aria-expanded={open}
        aria-label={ariaLabel}
        aria-labelledby={ariaLabelledBy}
        className={cn(
          "flex w-full items-center justify-between rounded-lg border border-input bg-transparent py-2 pr-2 pl-2.5 text-sm h-8",
          className,
        )}
      >
        <span className={cn(!value && "text-muted-foreground")}>
          {value ? formatCategoryName(value) : placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" />
      </PopoverTrigger>
      <PopoverContent className="w-(--anchor-width) p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            ref={inputRef}
            placeholder={
              addMode ? "Type the new category name..." : "Search categories..."
            }
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            {addMode ? (
              <CommandGroup>
                <CommandItem
                  disabled={search.trim() === ""}
                  onSelect={() => {
                    const name = search.trim();
                    if (!name) return;
                    commit(name, true);
                  }}
                >
                  <Plus className="size-4" />
                  {search.trim() ? `Create "${search.trim()}"` : "Create"}
                </CommandItem>
              </CommandGroup>
            ) : (
              <>
                {filtered.length === 0 && !showCreate && (
                  <CommandEmpty>No categories found.</CommandEmpty>
                )}
                <CommandGroup>
                  {filtered.map((c) => (
                    <CommandItem
                      key={c.id}
                      value={c.name}
                      onSelect={() => commit(c.name, false)}
                    >
                      {formatCategoryName(c.name)}
                    </CommandItem>
                  ))}
                </CommandGroup>
                {onCreateCategory && (
                  <>
                    <CommandSeparator />
                    <CommandGroup>
                      {showCreate ? (
                        <CommandItem
                          onSelect={() => commit(search.trim(), true)}
                        >
                          <Plus className="size-4" />
                          Create "{search.trim()}"
                        </CommandItem>
                      ) : (
                        <CommandItem
                          onSelect={() => {
                            setSearch("");
                            setAddMode(true);
                            setTimeout(() => inputRef.current?.focus(), 0);
                          }}
                        >
                          <Plus className="size-4" />
                          Add new category
                        </CommandItem>
                      )}
                    </CommandGroup>
                  </>
                )}
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
